import React, { useEffect, useState } from 'react';
import { Form, Button, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { spotifySettingsApi } from '../api/api';
import { toast } from 'react-toastify';

export default function PlaylistPicker({ value, onChange, label, allowCreate = false }) {
  const [playlists, setPlaylists] = useState([]);
  const [filteredPlaylists, setFilteredPlaylists] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [customId, setCustomId] = useState('');

  // Load playlists on mount
  useEffect(() => {
    fetchPlaylists();
  }, []);

  // Filter playlists when search or playlists change
  useEffect(() => {
    const filtered = playlists.filter((p) =>
      p.name.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredPlaylists(filtered);
  }, [search, playlists]);

  const fetchPlaylists = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await spotifySettingsApi.getPlaylists();
      setPlaylists(res.data.playlists || []);
    } catch (err) {
      setError('Konnte Playlists nicht laden. Stelle sicher, dass Spotify verbunden ist.');
      console.error('Error fetching playlists:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlaylist = async () => {
    if (!newPlaylistName.trim()) {
      toast.error('Bitte gib einen Namen für die neue Playlist ein');
      return;
    }

    setIsCreating(true);
    try {
      const res = await spotifySettingsApi.createPlaylist(newPlaylistName);
      const newPlaylist = res.data.playlist;
      
      // Add to list
      setPlaylists((prev) => [...prev, newPlaylist]);
      
      // Select it
      onChange(newPlaylist.id);
      
      // Reset form
      setNewPlaylistName('');
      setShowCreateForm(false);
      
      toast.success(`Playlist "${newPlaylist.name}" erstellt!`);
    } catch (err) {
      toast.error('Fehler beim Erstellen der Playlist');
      console.error('Error creating playlist:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSelectPlaylist = (playlistId) => {
    onChange(playlistId);
    setCustomId('');
  };

  const handleCustomIdChange = (e) => {
    const id = e.target.value;
    setCustomId(id);
    if (id.trim()) {
      onChange(id.trim());
    }
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label className="small">{label}</Form.Label>

      {error && <Alert variant="warning" className="small mb-2">{error}</Alert>}

      {/* Suchfeld */}
      <InputGroup size="sm" className="mb-2">
        <Form.Control
          placeholder="Playlist suchen..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-dark text-white border-secondary"
          disabled={loading}
        />
        {loading && <Spinner animation="border" size="sm" />}
        <Button variant="outline-secondary" onClick={fetchPlaylists} disabled={loading}>
          🔄
        </Button>
      </InputGroup>

      {/* Dropdown mit Playlists */}
      {!loading && playlists.length > 0 && (
        <Form.Select
          size="sm"
          value={value || ''}
          onChange={(e) => handleSelectPlaylist(e.target.value)}
          className="bg-dark text-white border-secondary mb-2"
        >
          <option value="">-- Keine Playlist ausgewählt --</option>
          {filteredPlaylists.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Form.Select>
      )}

      {!loading && playlists.length === 0 && (
        <Alert variant="info" className="small mb-2">
          Keine Playlists gefunden. Erstelle eine neue Playlist!
        </Alert>
      )}

      {/* Manuelle Playlist-ID */}
      <InputGroup size="sm" className="mb-2">
        <Form.Control
          placeholder="Oder manuelle Playlist-ID eingeben"
          value={customId}
          onChange={handleCustomIdChange}
          className="bg-dark text-white border-secondary"
        />
      </InputGroup>

      {/* Neue Playlist erstellen */}
      {allowCreate && (
        <div>
          {!showCreateForm ? (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setShowCreateForm(true)}
              className="w-100"
            >
              ➕ Neue Playlist erstellen
            </Button>
          ) : (
            <div className="p-2 bg-dark rounded border border-secondary">
              <InputGroup size="sm" className="mb-2">
                <Form.Control
                  placeholder="Name der neuen Playlist"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleCreatePlaylist()}
                  className="bg-dark text-white border-secondary"
                  disabled={isCreating}
                />
              </InputGroup>
              <div className="d-flex gap-2">
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleCreatePlaylist}
                  disabled={isCreating || !newPlaylistName.trim()}
                  className="flex-grow-1"
                >
                  {isCreating ? 'Erstelle...' : '✓ Erstellen'}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewPlaylistName('');
                  }}
                  disabled={isCreating}
                >
                  ✕ Abbrechen
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <Form.Text className="text-muted text-end d-block mt-1">
        {playlists.length} Playlists verfügbar
      </Form.Text>
    </Form.Group>
  );
}
